import React, { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import apiClient from '../utils/apiClient';
import { channelMeta } from '../utils/channels';

// What the AI costs, how fast it is, and whether it is closing anything.
//
// The two halves are deliberately on ONE page. Spend on its own makes every
// improvement look like an expense; conversion on its own makes every model
// upgrade look free. The question anyone actually opens this page with is
// "is the extra intelligence worth the extra money", and only the two
// together answer it.
//
// Every number here is honest about what it is not: the cost is list pricing,
// not an invoice, and a quote that is still open is shown as maturing rather
// than counted as a loss. An overstated conversion rate would be the most
// expensive bug on this page.

const DAY_PRESETS = [
  { label: '7 days', days: 7 },
  { label: '14 days', days: 14 },
  { label: '30 days', days: 30 },
];

const PURPOSE_LABELS = {
  situation: 'Reading the situation',
  classify: 'Classifying the message',
  extract: 'Pulling out details',
  compose: 'Writing the reply',
  vision: 'Reading a photo',
  facebook_reply: 'Facebook reply',
  nuance: 'Tone check',
  consultant: 'Consultant assist',
};

// en-GB grouping, not en-ZA. The South African convention writes R68 293,94
// with a decimal comma, which sits one glance away from being misread as
// sixty-eight thousand two hundred and ninety-four — and every other money
// figure in this dashboard (the quote total in the review modal, the job
// lines) already renders as R68,293.94. Matching them matters more here than
// matching the locale.
function money(v) {
  if (v === null || v === undefined) return '—';
  return `R${Number(v).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function msLabel(ms) {
  if (ms === null || ms === undefined) return '—';
  return ms >= 1000 ? `${(ms / 1000).toFixed(1)}s` : `${ms}ms`;
}

function pct(v) {
  return v === null || v === undefined ? '—' : `${v}%`;
}

function Card({ title, value, sub, tone = 'text-gray-900' }) {
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4">
      <div className="text-xs uppercase tracking-wide text-gray-500">{title}</div>
      <div className={`text-3xl font-semibold mt-1 ${tone}`}>{value}</div>
      {sub && <div className="text-xs text-gray-500 mt-1">{sub}</div>}
    </div>
  );
}

export default function AiUsage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [days, setDays] = useState(7);
  const [channel, setChannel] = useState('');
  const [failed, setFailed] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [autonomy, setAutonomy] = useState(null);

  const params = useMemo(() => {
    const p = new URLSearchParams();
    p.set('days', String(days));
    if (channel) p.set('channel', channel);
    return p;
  }, [days, channel]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const { data: d } = await apiClient.get(`/api/v1/ai-metrics/summary?${params}`);
      setData(d);
      // Separate request, deliberately: the autonomy panel is newer than the
      // rest of the page, and a backend that predates it must still render
      // everything else rather than showing an error for the whole tab.
      try {
        const { data: a } = await apiClient.get(`/api/v1/ai-metrics/autonomy?${params}`);
        setAutonomy(a);
      } catch (_) { setAutonomy(null); }
    } catch (e) {
      // Distinct from "no data": telling someone the AI cost nothing when the
      // request actually failed is worse than telling them nothing at all.
      setLoadError(true);
      toast.error('Could not load AI usage');
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const loadFailed = async () => {
    if (failed) { setFailed(null); return; }
    try {
      const p = new URLSearchParams(params);
      p.set('only_failed', 'true');
      p.set('limit', '50');
      const { data: d } = await apiClient.get(`/api/v1/ai-metrics/calls?${p}`);
      setFailed(d);
    } catch (e) {
      toast.error('Could not load those calls');
    }
  };

  // Asks Brain now rather than waiting for the hourly sweep. Bounded server
  // side, so leaning on it cannot turn into a stampede of lookups.
  const refreshOutcomes = async () => {
    setRefreshing(true);
    try {
      const { data: r } = await apiClient.post('/api/v1/ai-metrics/refresh-outcomes');
      const t = r?.tally || {};
      toast.success(t.checked ? `Checked ${t.checked} quotes` : 'Everything already up to date');
      fetchAll();
    } catch (e) {
      toast.error('Could not reach Brain');
    } finally {
      setRefreshing(false);
    }
  };

  const usage = data?.usage || {};
  const outcomes = data?.outcomes || {};
  const ratios = data?.ratios || {};

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">AI usage</h1>
          <p className="text-sm text-gray-500">
            What the assistant costs, how fast it replies, and what it closes.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {DAY_PRESETS.map((p) => (
            <button
              key={p.days}
              onClick={() => setDays(p.days)}
              className={`px-3 py-1.5 rounded text-sm border ${
                days === p.days
                  ? 'bg-indigo-600 text-white border-indigo-600'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              }`}
            >
              {p.label}
            </button>
          ))}
          <select
            value={channel}
            onChange={(e) => setChannel(e.target.value)}
            className="px-2 py-1.5 border border-gray-300 rounded text-sm bg-white"
          >
            <option value="">All channels</option>
            <option value="email">Email</option>
            <option value="whatsapp">WhatsApp</option>
            <option value="facebook">Facebook</option>
          </select>
        </div>
      </div>

      {loading ? (
        <div className="py-16 text-center text-gray-500">Loading…</div>
      ) : loadError ? (
        <div className="bg-white border border-red-200 rounded-lg p-10 text-center">
          <p className="text-red-700 font-medium">Couldn't load AI usage.</p>
          <button onClick={fetchAll}
                  className="mt-3 px-3 py-1.5 text-sm rounded bg-indigo-600 text-white">
            Try again
          </button>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card
              title="AI spend"
              value={money(usage.cost_rand)}
              sub={`${usage.calls || 0} calls · ${(usage.tokens || 0).toLocaleString()} tokens${
                usage.cost_is_floor ? ' · at least' : ''}`}
            />
            <Card
              title="Quotes accepted"
              value={pct(outcomes.conversion)}
              tone="text-emerald-600"
              sub={
                outcomes.conversion === null || outcomes.conversion === undefined
                  ? `${outcomes.quotes || 0} sent · none settled yet`
                  : `${outcomes.accepted} of ${outcomes.accepted + outcomes.lost} settled${
                      outcomes.maturing ? ` · ${outcomes.maturing} still open` : ''}`
              }
            />
            <Card
              title="Money won"
              value={money(outcomes.accepted_rand)}
              sub={`of ${money(outcomes.quoted_rand)} quoted`}
            />
            {/* The line that settles the argument about spending more on a
                model. Low single digits means more intelligence is cheap. */}
            <Card
              title="AI cost of that"
              value={pct(ratios.cost_as_pct_of_accepted)}
              tone={
                ratios.cost_as_pct_of_accepted !== null
                && ratios.cost_as_pct_of_accepted !== undefined
                && ratios.cost_as_pct_of_accepted > 10
                  ? 'text-yellow-600' : 'text-gray-900'
              }
              sub={`${money(ratios.cost_per_quote)} per quote · ${money(ratios.cost_per_accepted)} per deal`}
            />
          </div>

          {/* The caveats travel WITH the numbers rather than living in a wiki
              nobody opens. A figure read without them gets quoted in a meeting
              as something it isn't. */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-xs text-amber-900 space-y-1">
            <div>
              <strong>Cost is an estimate</strong> — public list prices at a fixed USD/ZAR
              rate, not a provider invoice.
              {usage.cost_is_floor && (
                <> A model with no price on file appeared in this period, so the
                total is a <strong>floor</strong>, not an estimate.</>
              )}
            </div>
            <div>
              <strong>Conversion counts settled quotes only.</strong> A quote sent this week
              hasn't had time to be accepted, so it's shown as still open rather than
              counted as a loss.
              {outcomes.median_days_to_accept !== null
                && outcomes.median_days_to_accept !== undefined && (
                <> Customers typically say yes within {outcomes.median_days_to_accept} day
                {outcomes.median_days_to_accept === 1 ? '' : 's'}, so a window shorter
                than that won't show a real rate.</>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-2 pt-1">
              <span>
                Outcomes last checked with Brain:{' '}
                {outcomes.last_checked
                  ? new Date(outcomes.last_checked).toLocaleString('en-GB', {
                      day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })
                  : 'never'}
              </span>
              <button
                onClick={refreshOutcomes}
                disabled={refreshing}
                className="px-2 py-1 rounded border border-amber-300 bg-white hover:bg-amber-100 disabled:opacity-50"
              >
                {refreshing ? 'Checking…' : 'Check now'}
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Costliest first: that ordering IS the prioritisation. */}
            <div className="bg-white border border-gray-200 rounded-lg">
              <div className="px-4 py-3 border-b border-gray-200">
                <h2 className="font-semibold text-gray-900">Where the money goes</h2>
                <p className="text-xs text-gray-500">Costliest first.</p>
              </div>
              {(usage.by_purpose || []).length === 0 ? (
                <div className="p-6 text-sm text-gray-500">No AI calls in this period.</div>
              ) : (
                <ul className="divide-y divide-gray-100">
                  {(usage.by_purpose || []).map((p) => {
                    const share = usage.cost_rand
                      ? Math.round((p.cost_rand / usage.cost_rand) * 100) : 0;
                    return (
                      <li key={p.purpose} className="px-4 py-3">
                        <div className="flex items-center justify-between gap-4">
                          <span className="text-sm font-medium text-gray-900">
                            {PURPOSE_LABELS[p.purpose] || p.purpose}
                          </span>
                          <span className="text-sm text-gray-600 whitespace-nowrap">
                            {money(p.cost_rand)} · {share}%
                          </span>
                        </div>
                        <div className="mt-1.5 h-1.5 bg-gray-100 rounded">
                          <div className="h-1.5 bg-indigo-500 rounded" style={{ width: `${share}%` }} />
                        </div>
                        <div className="mt-1 text-xs text-gray-400">
                          {p.calls} calls · median {msLabel(p.p50_ms)} · p90 {msLabel(p.p90_ms)}
                          {p.failed > 0 && <span className="text-red-500"> · {p.failed} failed</span>}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            {/* Spend and conversion side by side per channel: the comparison
                that says where the assistant is earning its keep. */}
            <div className="bg-white border border-gray-200 rounded-lg">
              <div className="px-4 py-3 border-b border-gray-200">
                <h2 className="font-semibold text-gray-900">By channel</h2>
                <p className="text-xs text-gray-500">What each one costs, and what it closes.</p>
              </div>
              {(outcomes.by_channel || []).length === 0 && (usage.by_channel || []).length === 0 ? (
                <div className="p-6 text-sm text-gray-500">Nothing recorded in this period.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50 text-gray-500 text-xs">
                      <tr>
                        <th className="px-3 py-2 text-left">Channel</th>
                        <th className="px-3 py-2 text-right">Spend</th>
                        <th className="px-3 py-2 text-right">Quotes</th>
                        <th className="px-3 py-2 text-right">Accepted</th>
                        <th className="px-3 py-2 text-right">Won</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {Array.from(new Set([
                        ...(usage.by_channel || []).map((c) => c.channel),
                        ...(outcomes.by_channel || []).map((c) => c.channel),
                      ])).map((name) => {
                        const u = (usage.by_channel || []).find((c) => c.channel === name) || {};
                        const o = (outcomes.by_channel || []).find((c) => c.channel === name) || {};
                        const meta = channelMeta(name);
                        return (
                          <tr key={name} className="hover:bg-gray-50">
                            <td className="px-3 py-2">
                              <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${meta.classes}`}>
                                {meta.label}
                              </span>
                            </td>
                            <td className="px-3 py-2 text-right">{money(u.cost_rand)}</td>
                            <td className="px-3 py-2 text-right">{o.quotes ?? '—'}</td>
                            <td className="px-3 py-2 text-right">
                              {o.conversion === null || o.conversion === undefined
                                ? <span className="text-gray-400">too early</span>
                                : pct(o.conversion)}
                            </td>
                            <td className="px-3 py-2 text-right">{money(o.accepted_rand)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          {/* How much of the work the assistant is doing, and what stops the
              rest. The headline is what WOULD send, not what did: while
              auto-send is off everything queues, so the achieved rate says
              nothing about what is achievable — and turning it on to find out
              is the wrong order to learn it in. */}
          {autonomy && autonomy.totals.replies > 0 && (
            <div className="bg-white border border-gray-200 rounded-lg">
              <div className="px-4 py-3 border-b border-gray-200 flex flex-wrap items-baseline justify-between gap-2">
                <div>
                  <h2 className="font-semibold text-gray-900">How much runs unattended</h2>
                  <p className="text-xs text-gray-500">
                    {autonomy.totals.replies} replies · {autonomy.totals.sent} sent
                    without a person
                  </p>
                </div>
                <div className="text-right">
                  <div className="text-3xl font-semibold text-indigo-600">
                    {pct(autonomy.totals.would_send_rate)}
                  </div>
                  <div className="text-xs text-gray-500">
                    would send at today's settings
                  </div>
                </div>
              </div>

              <div className="p-4 space-y-4">
                {autonomy.totals.sent === 0 && (
                  <p className="text-xs text-gray-500">
                    Auto-send is off, so everything queued. The figure above is what
                    would have gone out unattended if it were on — measured while
                    it is off, so the decision is made on evidence rather than nerve.
                  </p>
                )}

                {(autonomy.by_gate || []).length > 0 && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-900 mb-2">
                      What holds the rest back
                    </h3>
                    <ul className="space-y-2">
                      {autonomy.by_gate.map((g) => {
                        const share = autonomy.totals.replies
                          ? Math.round((g.count / autonomy.totals.replies) * 100) : 0;
                        return (
                          <li key={g.reason_code}>
                            <div className="flex items-center justify-between gap-3 text-sm">
                              <span className="text-gray-900">
                                {g.label}
                                {/* A big number next to "complaint" is not an
                                    opportunity — it is the policy working. */}
                                {g.fixed && (
                                  <span className="ml-2 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-gray-100 text-gray-600">
                                    by design
                                  </span>
                                )}
                              </span>
                              <span className="text-gray-600 whitespace-nowrap">
                                {g.count} · {share}%
                              </span>
                            </div>
                            <div className="mt-1 h-1.5 bg-gray-100 rounded">
                              <div
                                className={`h-1.5 rounded ${g.fixed ? 'bg-gray-300' : 'bg-indigo-500'}`}
                                style={{ width: `${share}%` }}
                              />
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}

                {(autonomy.confidence_floor?.if_lowered || []).length > 0 && (
                  <div className="border-t border-gray-100 pt-3">
                    <h3 className="text-sm font-medium text-gray-900">
                      If the confidence floor moved
                    </h3>
                    <p className="text-xs text-gray-500 mb-2">
                      Currently {autonomy.confidence_floor.current ?? '—'}, holding back{' '}
                      {autonomy.confidence_floor.blocked_by_it} replies. Counted from the
                      scores we kept, not from lowering it and waiting a week.
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {autonomy.confidence_floor.if_lowered.map((f) => (
                        <div key={f.floor}
                             className="px-3 py-2 rounded border border-gray-200 text-sm">
                          <span className="font-medium">{f.floor}</span>
                          <span className="text-gray-500"> → +{f.would_release} replies</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <h2 className="font-semibold text-gray-900">How long customers wait</h2>
              <p className="text-xs text-gray-500 mb-3">
                Wall-clock time per AI call — what the customer waited, not the
                provider's own processing time.
              </p>
              <div className="grid grid-cols-3 gap-3 text-center">
                <div>
                  <div className="text-2xl font-semibold text-gray-900">{msLabel(usage.p50_ms)}</div>
                  <div className="text-xs text-gray-500">median</div>
                </div>
                <div>
                  <div className="text-2xl font-semibold text-gray-900">{msLabel(usage.p90_ms)}</div>
                  <div className="text-xs text-gray-500">9 in 10 under</div>
                </div>
                <div>
                  <div className="text-2xl font-semibold text-gray-900">{msLabel(usage.max_ms)}</div>
                  <div className="text-xs text-gray-500">slowest</div>
                </div>
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <h2 className="font-semibold text-gray-900">Failed calls</h2>
              <p className="text-xs text-gray-500 mb-3">
                A failed call still cost the customer their wait. A run of them is
                what a provider outage looks like from here.
              </p>
              <div className="flex items-baseline gap-3">
                <span className={`text-3xl font-semibold ${
                  usage.failed ? 'text-red-600' : 'text-gray-900'}`}>
                  {usage.failed || 0}
                </span>
                <span className="text-sm text-gray-500">{pct(usage.failure_rate)} of calls</span>
                {usage.failed > 0 && (
                  <button onClick={loadFailed}
                          className="ml-auto text-sm text-indigo-600 hover:underline">
                    {failed ? 'Hide' : 'Show them'}
                  </button>
                )}
              </div>
              {failed && (
                <div className="mt-3 max-h-64 overflow-y-auto border border-gray-200 rounded">
                  <table className="min-w-full text-xs">
                    <tbody className="divide-y divide-gray-100">
                      {(failed.items || []).map((c) => (
                        <tr key={c.id}>
                          <td className="px-2 py-1.5 whitespace-nowrap text-gray-500">
                            {new Date(c.created_at).toLocaleString('en-GB', {
                              day: '2-digit', month: 'short',
                              hour: '2-digit', minute: '2-digit' })}
                          </td>
                          <td className="px-2 py-1.5">{PURPOSE_LABELS[c.purpose] || c.purpose}</td>
                          <td className="px-2 py-1.5 text-gray-500">{msLabel(c.latency_ms)}</td>
                          <td className="px-2 py-1.5 text-red-600 truncate max-w-xs" title={c.error}>
                            {c.error || 'unknown error'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
